import { useQuery } from "@tanstack/react-query";
import type { Celebrity, CelebrityDifficulty, HealthResponse } from "@pushup/types";
import { ALL_DIFFICULTIES, DIFFICULTY_LABELS } from "@pushup/types";
import { Badge } from "@pushup/ui";
import { Activity, AudioLines, Dumbbell, Layers, ShieldCheck } from "lucide-react";
import { useCelebrities } from "@/features/celebrities/hooks";
import { api, WITH_CREDENTIALS } from "@/lib/api";
import { Breadcrumb } from "@/app/layout/breadcrumb";
import { Widget } from "@/app/layout/widget";

const HEALTH_KEY = ["health"] as const;

const DIFFICULTY_COLORS: Record<CelebrityDifficulty, string> = {
  easy: "bg-lb-lime",
  medium: "bg-lb-blue",
  hard: "bg-lb-orange",
  beast: "bg-lb-red",
};

function fetchHealth() {
  return api.get<HealthResponse>("/health", WITH_CREDENTIALS);
}

interface StatRowProps {
  label: string;
  value: string;
  barColor: string;
  barWidth: number;
}

function StatRow({ label, value, barColor, barWidth }: StatRowProps) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <p className="mb-1 text-[13px] font-semibold text-white">{label}</p>
        <div className="progress-lb">
          <div className={`progress-lb-bar ${barColor}`} style={{ width: `${barWidth}%` }} />
        </div>
      </div>
      <span className="stat-value">{value}</span>
    </div>
  );
}

export function DashboardPage() {
  const { data: celebrities, isLoading, isError } = useCelebrities();
  const healthQuery = useQuery({ queryKey: HEALTH_KEY, queryFn: fetchHealth });

  const list: Celebrity[] = celebrities ?? [];
  const total = list.length;
  const difficultyCounts = Object.fromEntries(
    ALL_DIFFICULTIES.map((d) => [d, list.filter((c) => c.difficulty === d).length]),
  ) as Record<CelebrityDifficulty, number>;
  const totalVoiceClips = list.reduce((sum, c) => sum + c.voicePhases.length, 0);
  const timedRounds = list.filter((c) => c.workoutDurationSec > 0).length;
  const avgPace = total > 0 ? list.reduce((sum, c) => sum + c.secondsPerRep, 0) / total : 0;
  const avgRound =
    timedRounds > 0
      ? list
          .filter((c) => c.workoutDurationSec > 0)
          .reduce((sum, c) => sum + c.workoutDurationSec, 0) / timedRounds
      : 0;
  const maxCount = Math.max(1, ...ALL_DIFFICULTIES.map((d) => difficultyCounts[d]));

  return (
    <div>
      <Breadcrumb
        items={[{ label: "App" }, { label: "Main" }, { label: "Dashboard", active: true }]}
      />

      <h1 className="page-title-lb">
        Dashboard <small>The Pushup Tracker</small>
      </h1>

      <div className="grid gap-5 lg:grid-cols-3">
        <Widget
          title={
            <h6 className="flex items-center gap-2 text-xs font-bold tracking-wider text-foreground/80">
              CELEBRITY <span className="font-semibold">OPPONENTS</span>
            </h6>
          }
          settings
          refresh
          className="lg:col-span-2"
        >
          {isLoading ? (
            <p className="py-6 text-sm text-foreground/50">Loading opponents…</p>
          ) : isError ? (
            <p className="py-6 text-sm text-lb-red">Couldn&apos;t load celebrity data.</p>
          ) : (
            <>
              <div className="mb-5 flex items-end justify-between">
                <div>
                  <p className="text-4xl font-light text-white">{total}</p>
                  <p className="text-sm text-foreground/50">total opponents available</p>
                </div>
                <div className="flex gap-2">
                  {ALL_DIFFICULTIES.map((d) => (
                    <span key={d} className="flex items-center gap-1.5 text-xs text-foreground/60">
                      <span className={`size-2.5 rounded-full ${DIFFICULTY_COLORS[d]}`} />
                      {DIFFICULTY_LABELS[d]}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                {ALL_DIFFICULTIES.map((d) => (
                  <StatRow
                    key={d}
                    label={DIFFICULTY_LABELS[d]}
                    value={String(difficultyCounts[d])}
                    barColor={DIFFICULTY_COLORS[d]}
                    barWidth={total === 0 ? 0 : (difficultyCounts[d] / maxCount) * 100}
                  />
                ))}
              </div>
            </>
          )}
        </Widget>

        <Widget
          title={<h6 className="text-xs font-bold tracking-wider">BACKEND STATUS</h6>}
          settings
          close
        >
          {healthQuery.isLoading ? (
            <p className="py-4 text-sm text-foreground/50">Checking…</p>
          ) : healthQuery.isError ? (
            <p className="py-4 text-sm text-lb-red">Backend unreachable.</p>
          ) : healthQuery.data ? (
            <>
              <div className="mb-4 flex items-center gap-2">
                <ShieldCheck className="size-5 text-lb-lime" />
                <span className="text-lg font-semibold text-white">Live</span>
              </div>
              <div className="space-y-2 text-sm text-foreground/60">
                <p>
                  Status: <strong className="text-white">ok</strong>
                </p>
                <p>
                  Database: <strong className="text-white">connected</strong>
                </p>
                <p>
                  Version: <strong className="text-white">{healthQuery.data.version}</strong>
                </p>
              </div>
            </>
          ) : null}
        </Widget>

        <Widget
          title={<h6 className="text-xs font-bold tracking-wider">ROUND PACING</h6>}
          settings
          close
        >
          <div className="space-y-4">
            <StatRow
              label="Average seconds / rep"
              value={avgPace.toFixed(1)}
              barColor="bg-lb-blue"
              barWidth={avgPace > 0 ? Math.min(100, (avgPace / 8) * 100) : 0}
            />
            <StatRow
              label="Timed rounds"
              value={`${timedRounds}/${total}`}
              barColor="bg-lb-lime"
              barWidth={total === 0 ? 0 : (timedRounds / total) * 100}
            />
            <StatRow
              label="Average round length"
              value={avgRound > 0 ? `${Math.round(avgRound)}s` : "n/a"}
              barColor="bg-lb-orange"
              barWidth={avgRound > 0 ? Math.min(100, (avgRound / 300) * 100) : 0}
            />
          </div>
        </Widget>

        <Widget
          title={<h6 className="text-xs font-bold tracking-wider">VOICE PHASES</h6>}
          settings
          close
        >
          <div className="flex items-center gap-4">
            <span className="flex size-14 items-center justify-center rounded-full bg-lb-teal/25 text-white">
              <AudioLines className="size-6" />
            </span>
            <div>
              <p className="text-4xl font-light text-white">{totalVoiceClips}</p>
              <p className="text-sm text-foreground/50">audio clips uploaded</p>
            </div>
          </div>
        </Widget>

        <Widget
          title={<h6 className="text-xs font-bold tracking-wider">SNAPSHOT</h6>}
          close
          className="lg:col-span-2"
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="flex items-center gap-3">
              <span className="flex size-11 items-center justify-center rounded-lg bg-lb-blue/20 text-white">
                <Layers className="size-5" />
              </span>
              <div>
                <p className="text-xl font-semibold text-white">
                  {list.reduce((s, c) => s + c.voicePhases.length, 0)}
                </p>
                <p className="text-xs text-foreground/50">total phases</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex size-11 items-center justify-center rounded-lg bg-lb-orange/20 text-white">
                <Dumbbell className="size-5" />
              </span>
              <div>
                <p className="text-xl font-semibold text-white">
                  {list.filter((c) => c.difficulty === "beast").length}
                </p>
                <p className="text-xs text-foreground/50">beast opponents</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex size-11 items-center justify-center rounded-lg bg-lb-lime/20 text-white">
                <Activity className="size-5" />
              </span>
              <div>
                <p className="text-xl font-semibold text-white">{timedRounds}</p>
                <p className="text-xs text-foreground/50">timed rounds</p>
              </div>
            </div>
          </div>
        </Widget>
      </div>

      <h2 className="mb-3 mt-8 text-lg font-light text-white">Latest opponents</h2>
      <div className="widget">
        <div className="widget-body">
          {isLoading ? (
            <p className="py-4 text-sm text-foreground/50">Loading…</p>
          ) : list.length === 0 ? (
            <p className="py-4 text-sm text-foreground/50">
              No celebrities yet. Add your first opponent above.
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {list.slice(0, 4).map((celebrity) => (
                <div
                  key={celebrity.id}
                  className="flex items-center gap-3 rounded-lg bg-white/5 p-3"
                >
                  {celebrity.imageDataUrl ? (
                    <img
                      src={celebrity.imageDataUrl}
                      alt={celebrity.name}
                      className="size-10 rounded-full object-cover"
                    />
                  ) : (
                    <span className="flex size-10 items-center justify-center rounded-full bg-lb-navy-soft text-base font-bold text-white">
                      {celebrity.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-white">{celebrity.name}</p>
                    <p className="text-xs text-foreground/50">
                      {celebrity.secondsPerRep}s/rep ·{" "}
                      {celebrity.workoutDurationSec > 0
                        ? `${celebrity.workoutDurationSec}s round`
                        : "endless"}
                    </p>
                  </div>
                  <Badge variant="secondary">{DIFFICULTY_LABELS[celebrity.difficulty]}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
